import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { agentMatchesAuthor } from './agentMatchesAuthor';
import type { ResponseAuthorIdentity } from './ResponseAuthorIdentity';
import { shouldShowAgentResponseHat } from './shouldShowAgentResponseHat';

const getNonEmptyString = (value: string | null | undefined): string | undefined => {
	const trimmed = value?.trim();
	return trimmed ? trimmed : undefined;
};

export type ResponseHatRenderStateParams = {
	/** Agent-mentions experiment kill-switch. When off, the hat is never rendered. */
	shouldEnableAgentResponseHat: boolean;
	/** The thinking indicator has handed off, i.e. the answer is about to / is streaming. */
	canRenderAfterThinking: boolean;
	/** Identity of the author of THIS response (optimistic at submit, backend at FINAL_RESPONSE). */
	currentResponseAuthor: ResponseAuthorIdentity;
	/** Identity of the previous assistant turn, used to detect a meaningful author transition. */
	previousAssistantAuthor?: ResponseAuthorIdentity;
	/** Result of the by-agent-id lookup for this author (may still be hydrating / undefined). */
	responseAuthorAgent: Agent | undefined;
	/** Identity account id carried on the (optimistic or backend) message author, if any. */
	backendAuthorIdentityAccountId: string | undefined;
	/** The conversation is actively generating a response right now. */
	isConversationActivelyGenerating: boolean;
	/** This message's stream has completed (FINAL_RESPONSE received and settled). */
	streamingComplete: boolean;
	/** Whether any assistant answer text/content has started rendering for this turn. */
	hasAnswerContent: boolean;
};

export type ResponseHatRenderState = {
	/** Whether to render the response hat at all this turn. */
	shouldRenderAgentResponseHat: boolean;
	/** Whether the hat should show the default Rovo identity. */
	isDefaultRovo: boolean;
	/** Resolved identity account id for the specialist author (undefined for default / still hydrating). */
	agentIdentityAccountId: string | undefined;
	/** Reserve a layout-stable skeleton while the specialist identity is still hydrating. */
	isIdentityHydrating: boolean;
};

/**
 * Pure decision for the streaming response hat, extracted from StoreAssistantView so the
 * (subtle) precedence and hydration-window rules are testable in isolation and don't bloat
 * the render component.
 *
 * Key rules encoded here:
 * - Kill-switch parity: with the gate off, nothing renders (equivalent to pre-feature).
 * - Identity precedence: the author's own identity account id wins over the by-agent-id
 *   lookup, so the value we already know at submit (optimistic author) is used immediately.
 * - Hydration window: the answer streams as author-less ANSWER_PART events, so the hat must
 *   NOT be gated on the FINAL_RESPONSE metric window. While a specialist turn is streaming and
 *   its identity account id is not yet known, reserve a skeleton (avoid pop-in reflow and a
 *   misleading default-agent flash). Once the identity is known, render the real avatar and let
 *   Relay hydrate the name. Default authors are never treated as hydrating.
 */
export const getResponseHatRenderState = ({
	shouldEnableAgentResponseHat,
	canRenderAfterThinking,
	currentResponseAuthor,
	previousAssistantAuthor,
	responseAuthorAgent,
	backendAuthorIdentityAccountId,
	isConversationActivelyGenerating,
	streamingComplete,
	hasAnswerContent,
}: ResponseHatRenderStateParams): ResponseHatRenderState => {
	const isDefaultRovo = currentResponseAuthor.isDefault;

	// The by-agent-id lookup is only trustworthy if it actually resolved to THIS author.
	const responseAuthorAgentIdentityAccountId = agentMatchesAuthor(
		responseAuthorAgent,
		currentResponseAuthor,
	)
		? getNonEmptyString(responseAuthorAgent?.identity_account_id)
		: undefined;
	// Default authors never carry a specialist identity account id.
	const authorIdentityAccountId = isDefaultRovo
		? undefined
		: getNonEmptyString(backendAuthorIdentityAccountId);
	// Author's own identity wins over the lookup so the submit-time snapshot is used immediately.
	const agentIdentityAccountId = authorIdentityAccountId ?? responseAuthorAgentIdentityAccountId;

	// Deliberately scoped to the in-flight turn (not the FINAL_RESPONSE metric window), because
	// the answer streams as author-less ANSWER_PART events before `streamingComplete`.
	const isResponseStreamingActive = isConversationActivelyGenerating && !streamingComplete;
	const shouldReservePreAnswerSkeleton = isResponseStreamingActive && !hasAnswerContent;
	const isIdentityHydrating =
		!isDefaultRovo &&
		Boolean(currentResponseAuthor.id) &&
		isResponseStreamingActive &&
		(!agentIdentityAccountId || shouldReservePreAnswerSkeleton);

	const hasCurrentResponseAuthorIdentity = Boolean(
		currentResponseAuthor.id || agentIdentityAccountId,
	);
	const canDisplayCurrentResponseAuthor = isDefaultRovo || hasCurrentResponseAuthorIdentity;
	const hasMeaningfulAuthorTransition = shouldShowAgentResponseHat({
		currentAuthor: currentResponseAuthor,
		previousAuthor: previousAssistantAuthor,
	});

	// Reserve the hat before the first ANSWER_PART when a turn is already actively
	// generating. The thinking indicator's explicit handoff can lag until/after text
	// starts streaming; waiting for it lets the response text render first and then
	// jump downward when the hat appears.
	const hasResponseHatHandoff = canRenderAfterThinking;
	const shouldRenderAgentResponseHat =
		shouldEnableAgentResponseHat &&
		hasResponseHatHandoff &&
		canDisplayCurrentResponseAuthor &&
		hasMeaningfulAuthorTransition;

	return {
		shouldRenderAgentResponseHat,
		isDefaultRovo,
		agentIdentityAccountId,
		isIdentityHydrating,
	};
};
